import type {
  AccountingInvoiceJson,
  ApplyCashApplicationRequest,
  CashApplicationPreview,
  InvoiceAssignmentInput,
  InvoiceAssignmentLine,
} from "@/api/accounting.tsx"
import {
  build_unresolved_prepayments_error,
  compute_apply_balance,
  compute_settlement_breakdown,
  find_overpaid_invoices,
  plan_bi_allowance_rows,
  QbCustomerPick,
  seed_auto_assignments_for_apply_page,
  SettlementBreakdownRow,
  summarize_invoice_links,
} from "@/accounting/cash_application_display.ts"
import { exactCurrencyFormatter } from "@/utils"

/** What the bulk run decided for one check before touching the accounting system. */
export type BulkApplyPlan =
  | { ready: true; request: ApplyCashApplicationRequest }
  /** Left for a person: `reason` says what the Apply Cash page will ask them to decide. */
  | { ready: false; reason: string }

export interface BulkApplyInput {
  preview: CashApplicationPreview
  /** The accounting customer matched for the check, or null when no match was confident enough. */
  customer: QbCustomerPick | null
  /** That customer's invoices (plus the check's own), as the Apply Cash page loads them. */
  invoices: AccountingInvoiceJson[]
  /** The deposit ("bank") account every payment in the run posts to. */
  deposit_account_remote_id: string
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`
}

function skip(reason: string): BulkApplyPlan {
  return { ready: false, reason }
}

/**
 * Decide whether one check can be applied with no decision from a person, and if so build the
 * request the Apply Cash page would have sent with every default left alone.
 *
 * Bulk applies only what needs no judgement: the customer and every invoice match on their own,
 * every line is coded, and the invoices equal cash + credit memo to the cent once a configured
 * allowance (KeHE's BI, Walmart's off-invoice) is written off. Anything the page would put a
 * checkbox or a picker in front of — a short pay, an additional discount, a customer per vendor
 * account, an unmatched invoice — is skipped with the reason, never assumed. The server re-checks
 * the same balance on every apply, so this is the gate for sending, not the only guard.
 */
export function plan_bulk_apply({ preview, customer, invoices, deposit_account_remote_id }: BulkApplyInput): BulkApplyPlan {
  const system = preview.accounting_system
  if (preview.existing_credit_memo_id) {
    return skip("A credit memo already exists for this check. Open it to finish applying.")
  }
  if ((preview.customer_groups?.length ?? 0) > 0 || (preview.dc_groups?.length ?? 0) > 0 || preview.dc_plan_error) {
    return skip(`This check is applied customer by customer. Open it to choose the ${system} customers.`)
  }
  if (!customer) {
    return skip(`No ${system} customer could be matched to this check automatically.`)
  }
  const uncoded = preview.uncoded_transactions ?? []
  if (uncoded.length > 0) {
    return skip(`${plural(uncoded.length, "transaction")} on this check ${uncoded.length === 1 ? "is" : "are"} not coded.`)
  }
  const overcoded = preview.overcoded_transactions ?? []
  if (overcoded.length > 0) {
    return skip(`${plural(overcoded.length, "transaction")} on this check ${overcoded.length === 1 ? "is" : "are"} coded for more than the check carries.`)
  }
  if (build_unresolved_prepayments_error(preview.prepayments ?? []) !== "") {
    return skip("A prepayment line on this check could not be matched to its invoice.")
  }
  const gl_code_issues = preview.gl_code_issues ?? []
  if (gl_code_issues.length > 0) {
    return skip(`Coded to accounts ${system} won't accept: ${gl_code_issues.join(", ")}.`)
  }
  if (preview.repayment_invoice && !preview.repayment_invoice.supported) {
    return skip(`The customer is paying money back on this check, which ${system} can't take as a payment.`)
  }

  const initial: Record<string, string> = {}
  for (const line of preview.invoice_assignments) {
    if (line.accounting_invoice_id) {
      initial[line.deduction_id] = line.accounting_invoice_id
    }
  }
  const invoice_kind_lines = preview.invoice_assignments.filter((l: InvoiceAssignmentLine) => l.kind === "invoice")
  const assignments = invoices.length > 0 ? seed_auto_assignments_for_apply_page(invoice_kind_lines, invoices, initial) : initial

  const balance = compute_apply_balance(preview, assignments, invoices)
  if (balance.unassigned_count > 0) {
    return skip(
      `${balance.unassigned_count} of ${plural(balance.invoice_lines.length, "invoice")} could not be matched to a ${system} invoice.`
    )
  }
  if (balance.invoice_lines.length === 0 && preview.credit_memo_lines.length === 0) {
    return skip("Nothing on this check to apply.")
  }

  // The same breakdown the page builds: each invoice's gap, less what a configured allowance
  // writes off. Whatever is still short after that needs a person (short pay or discount).
  const check_deduction_ids = new Set(preview.invoice_assignments.map((l: InvoiceAssignmentLine) => l.deduction_id))
  const breakdown = compute_settlement_breakdown(
    balance.invoice_lines,
    assignments,
    balance.accounting_invoice_by_id,
    summarize_invoice_links(invoices, check_deduction_ids),
    balance.payment_balance_delta_cents,
    balance.credited_by_invoice
  )
  const bi = preview.bi_allowance ?? null
  const bi_plan = bi
    ? plan_bi_allowance_rows(breakdown, balance.accounting_invoice_by_id, assignments, bi.percentage)
    : { rows: [], remaining: breakdown }
  if (bi_plan.remaining === null) {
    if (balance.payment_balance_delta_cents !== 0) {
      return skip(
        `The invoices and the payment differ by ${exactCurrencyFormatter(Math.abs(balance.payment_balance_delta_cents) / 100)}. Open the check to review it.`
      )
    }
  } else {
    const short_cents = bi_plan.remaining.reduce((sum: number, row: SettlementBreakdownRow) => sum + row.short_cents + row.over_cents, 0)
    if (short_cents !== 0) {
      const short_rows = bi_plan.remaining.filter((row: SettlementBreakdownRow) => row.short_cents + row.over_cents !== 0)
      return skip(
        `${plural(short_rows.length, "invoice")} paid short by ${exactCurrencyFormatter(short_cents / 100)} in total. Open the check to decide how to settle it.`
      )
    }
  }

  const selected = balance.invoice_lines
    .map((line: InvoiceAssignmentLine) => balance.accounting_invoice_by_id.get(assignments[line.deduction_id] ?? ""))
    .filter((inv): inv is AccountingInvoiceJson => inv !== undefined)
  const overpaid = find_overpaid_invoices(selected, balance.credited_by_invoice)
  if (overpaid.length > 0) {
    return skip(
      `${plural(overpaid.length, "invoice")} already ${overpaid.length === 1 ? "has" : "have"} a payment in ${system} (${overpaid
        .map((issue) => issue.invoice_number)
        .join(", ")}).`
    )
  }

  const invoice_assignments: InvoiceAssignmentInput[] = Object.entries(assignments)
    .filter((entry: [string, string]) => entry[1].length > 0)
    .map(([deduction_id, accounting_invoice_id]: [string, string]) => ({ deduction_id, accounting_invoice_id }))
  return {
    ready: true,
    request: {
      check_id: preview.check_id,
      customer_id: customer.remote_id,
      customer_name: customer.display_name,
      deposit_account_remote_id,
      // The check's own date, as the page defaults it. `check_date` is already yyyy-MM-dd.
      payment_date: preview.check_date ? preview.check_date.slice(0, 10) : undefined,
      invoice_assignments,
      book_bi_allowance: bi_plan.rows.length > 0 ? true : undefined,
    },
  }
}

/** One check's place in a bulk run. */
export type BulkApplyRowStatus = "queued" | "running" | "applied" | "skipped" | "failed"

export interface BulkApplyRow {
  check_number: string
  status: BulkApplyRowStatus
  /** Why it was skipped or failed; empty otherwise. */
  message: string
}

export interface BulkApplyCounts {
  applied: number
  skipped: number
  failed: number
  remaining: number
}

export function bulk_apply_counts(rows: BulkApplyRow[]): BulkApplyCounts {
  const count = (status: BulkApplyRowStatus) => rows.filter((row: BulkApplyRow) => row.status === status).length
  return { applied: count("applied"), skipped: count("skipped"), failed: count("failed"), remaining: count("queued") + count("running") }
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  const walmart: QbCustomerPick = { remote_id: "cust-1", display_name: "Walmart" }

  function invoice(id: string, number: string, total: number): AccountingInvoiceJson {
    return { id, remote_id: `r-${id}`, invoice_number: number, total_amount: total, amount_due: total, customer_id: "cust-1" } as AccountingInvoiceJson
  }

  function line(deduction_id: string, number: string, paid: number): InvoiceAssignmentLine {
    return { deduction_id, invoice_number: number, deduction_amount: paid, kind: "invoice", settle_amount: paid }
  }

  function preview(overrides: Partial<CashApplicationPreview>): CashApplicationPreview {
    return {
      check_id: "check-1",
      check_number: "4452784",
      check_date: "2026-09-23",
      check_amount: 0,
      invoice_assignments: [],
      unassigned_count: 0,
      credit_memo_lines: [],
      all_assigned: true,
      accounting_system: "Xero",
      early_pay_on_payment: false,
      ...overrides,
    } as CashApplicationPreview
  }

  const input = (p: CashApplicationPreview, invoices: AccountingInvoiceJson[], customer: QbCustomerPick | null = walmart): BulkApplyInput => ({
    preview: p,
    customer,
    invoices,
    deposit_account_remote_id: "bank-7928",
  })

  describe("plan_bulk_apply", () => {
    it("applies a check whose invoices equal its cash", () => {
      const p = preview({ check_amount: 357.6, invoice_assignments: [line("d1", "3314", 357.6)] })
      const plan = plan_bulk_apply(input(p, [invoice("x1", "INV-3314", 357.6)]))
      expect(plan).toEqual({
        ready: true,
        request: {
          check_id: "check-1",
          customer_id: "cust-1",
          customer_name: "Walmart",
          deposit_account_remote_id: "bank-7928",
          payment_date: "2026-09-23",
          invoice_assignments: [{ deduction_id: "d1", accounting_invoice_id: "x1" }],
          book_bi_allowance: undefined,
        },
      })
    })

    // Cadence's Walmart check 4452784: INV-3314 is $357.60 in Xero, Walmart pays $348.66.
    it("leaves a short-paid invoice for a person when no allowance is configured", () => {
      const p = preview({ check_amount: 348.66, invoice_assignments: [line("d1", "3314", 348.66)] })
      const plan = plan_bulk_apply(input(p, [invoice("x1", "INV-3314", 357.6)]))
      expect(plan).toEqual({
        ready: false,
        reason: "1 invoice paid short by $8.94 in total. Open the check to decide how to settle it.",
      })
    })

    it("applies the same check once the 2.5% allowance explains the short, and asks the server to book it", () => {
      const p = preview({
        check_amount: 348.66,
        invoice_assignments: [line("d1", "3314", 348.66)],
        bi_allowance: { gl_account_id: "4410", gl_account_name: "Walmart allowances", percentage: 2.5, label: "Walmart allowance" },
      })
      const plan = plan_bulk_apply(input(p, [invoice("x1", "INV-3314", 357.6)]))
      expect(plan.ready).toBe(true)
      expect(plan.ready && plan.request.book_bi_allowance).toBe(true)
    })

    it("still skips a short wider than the allowance", () => {
      const p = preview({
        check_amount: 300,
        invoice_assignments: [line("d1", "3314", 300)],
        bi_allowance: { gl_account_id: "4410", gl_account_name: "Walmart allowances", percentage: 2.5, label: "Walmart allowance" },
      })
      const plan = plan_bulk_apply(input(p, [invoice("x1", "INV-3314", 357.6)]))
      expect(plan).toMatchObject({ ready: false })
      expect(!plan.ready && plan.reason).toContain("paid short by $48.66")
    })

    it("skips when an invoice has no match, the customer is unknown, or a line is uncoded", () => {
      const p = preview({ check_amount: 100, invoice_assignments: [line("d1", "9999", 100)] })
      expect(plan_bulk_apply(input(p, [invoice("x1", "INV-3314", 100.5)]))).toEqual({
        ready: false,
        reason: "1 of 1 invoice could not be matched to a Xero invoice.",
      })
      expect(plan_bulk_apply(input(p, [], null))).toEqual({
        ready: false,
        reason: "No Xero customer could be matched to this check automatically.",
      })
      const uncoded = preview({
        check_amount: 90,
        invoice_assignments: [line("d1", "3314", 100)],
        uncoded_transactions: [{ deduction_id: "d2", invoice_number: "CB1", kind: "deduction", missing_amount: 10 }],
      })
      expect(plan_bulk_apply(input(uncoded, [invoice("x1", "INV-3314", 100)]))).toEqual({
        ready: false,
        reason: "1 transaction on this check is not coded.",
      })
    })

    it("applies a check with coded deductions when cash plus the memo equals the invoices", () => {
      const p = preview({
        check_amount: 90,
        invoice_assignments: [line("d1", "3314", 100), { deduction_id: "d2", invoice_number: "CB1", deduction_amount: -10, kind: "deduction" }],
        credit_memo_lines: [{ gl_account_id: "4400", gl_account_name: "Shortages", amount: 10 }],
      })
      expect(plan_bulk_apply(input(p, [invoice("x1", "INV-3314", 100)])).ready).toBe(true)
    })

    it("never re-applies a check that already has a credit memo or needs a customer per vendor account", () => {
      const with_memo = preview({ check_amount: 100, invoice_assignments: [line("d1", "3314", 100)], existing_credit_memo_id: "cm-1" })
      expect(plan_bulk_apply(input(with_memo, [invoice("x1", "INV-3314", 100)])).ready).toBe(false)
      const split = preview({
        check_amount: 100,
        invoice_assignments: [line("d1", "3314", 100)],
        customer_groups: [{ key: "061524", transaction_count: 1, invoice_cash: 100, deduction_total: 0, net: 100 }],
      })
      expect(plan_bulk_apply(input(split, [invoice("x1", "INV-3314", 100)])).ready).toBe(false)
    })
  })

  describe("bulk_apply_counts", () => {
    it("tallies a run", () => {
      const row = (status: BulkApplyRowStatus): BulkApplyRow => ({ check_number: "1", status, message: "" })
      expect(bulk_apply_counts([row("applied"), row("applied"), row("skipped"), row("failed"), row("queued"), row("running")])).toEqual({
        applied: 2,
        skipped: 1,
        failed: 1,
        remaining: 2,
      })
    })
  })
}
